// =============================================================================
// CONSTANTS.JS — Shared config, lookups, formatters, and tier/permission logic
// =============================================================================
// SECTIONS:
//   [STATUS]      Status config, flow, date-action labels, isPaidStatus()
//   [CREATORS]    Creator colors, socials, default agents
//   [AGENTS]      Agent colors
//   [MONTHS]      Month ordering
//   [FORMATTERS]  fmt(), fmtFull(), creatorInitials()
//   [COMMISSIONS] getAgentCommission(), getRelevantCommissions()
//   [TIERS]       Tier/plan feature gating (hasFeature, TIER_FEATURES)
// =============================================================================

// --- [STATUS] Status flow ---
// Concept -> In Production -> Waiting For Approval -> Approved -> Posted -> Invoice Sent -> Partially Paid -> Paid/Done
// All statuses count in revenue -- no exclusions
// "Partially Paid" = deposit received, commission only paid out at "Paid/Done"

import { commissionRowsFor } from './commissions'

export const STATUS_CONFIG = {
  'Concept':              { color: '#6b7280', bg: 'rgba(107,114,128,0.12)', dot: '#6b7280' },
  'In Production':        { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)',  dot: '#8b5cf6' },
  'Waiting For Approval': { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  dot: '#f59e0b' },
  'Approved':             { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',   dot: '#06b6d4' },
  'Posted':               { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  dot: '#3b82f6' },
  'Invoice Sent':         { color: '#f97316', bg: 'rgba(249,115,22,0.12)',  dot: '#f97316' },
  'Partially Paid':       { color: '#a855f7', bg: 'rgba(168,85,247,0.12)',  dot: '#a855f7' },
  'Paid/Done':            { color: '#10b981', bg: 'rgba(16,185,129,0.12)',  dot: '#10b981' },
  // Legacy statuses (for existing data)
  'Paid':                 { color: '#10b981', bg: 'rgba(16,185,129,0.12)',  dot: '#10b981' },
  'Done':                 { color: '#10b981', bg: 'rgba(16,185,129,0.12)',  dot: '#10b981' },
}

// Helper to check if a deal is in a "paid/done" state (handles legacy data)
export function isPaidStatus(status) {
  return status === 'Paid/Done' || status === 'Paid' || status === 'Done'
}

// Ordered for the status dropdown
export const ALL_STATUSES = [
  'Concept', 'In Production', 'Waiting For Approval', 'Approved', 'Posted', 'Invoice Sent', 'Partially Paid', 'Paid/Done'
]

// Status → date_action label mapping
// null means N/A (no date shown for that status)
export const DATE_ACTION_LABELS = {
  'Concept':              'Due Date',
  'In Production':        'Due Date',
  'Waiting For Approval': null,
  'Approved':             'Post Date',
  'Posted':               null,
  'Invoice Sent':         'Sent On',
  'Partially Paid':       null,
  'Paid/Done':            null,
}

export const CREATOR_COLORS = {
  'Malick':           '#f97316',
  'Mei-Anne':         '#8b5cf6',
  'Steve':            '#3b82f6',
  'Roua':             '#ec4899',
  'Hann-Dolf':        '#10b981',
  'Chance Twins':     '#f59e0b',
  'Subecca':          '#06b6d4',
  'Sandra':           '#84cc16',
  'Darcia':           '#a855f7',
  'Zhara':            '#14b8a6',
  'Unemployed Bros':  '#ef4444',
  'Revello':          '#f43f5e',
  'Evan':             '#fb923c',
  'Vanessa':          '#6366f1',
  'Loizza':           '#d946ef',
  'Ray Tarca':        '#0ea5e9',
  'Elizabeth':        '#f472b6',
  'Charlotte':        '#22d3ee',
  'Rebecca':          '#fb7185',
  'Sheldrick':        '#fbbf24',
  'Sarah':            '#c084fc',
}

// Social media links for all agency creators (from Webflow site)
export const CREATOR_SOCIALS = {
  'Steve':           { instagram: 'https://www.instagram.com/iamstevedaniel/',      tiktok: 'https://www.tiktok.com/@iamstevedaniels' },
  'Malick':          { instagram: 'https://www.instagram.com/malick.fr/',            tiktok: 'https://www.tiktok.com/@malick.fr' },
  'Mei-Anne':        { instagram: 'https://www.instagram.com/meiannelemieux/',       tiktok: 'https://www.tiktok.com/@meiannelemieux' },
  'Roua':            { instagram: 'https://www.instagram.com/soft.lyfee/',           tiktok: 'https://www.tiktok.com/@softlyfee' },
  'Darcia':          { instagram: 'https://www.instagram.com/darciadorilas/',        tiktok: 'https://www.tiktok.com/@darcia_dorilas/' },
  'Subecca':         { instagram: 'https://www.instagram.com/subecca.v',             tiktok: 'https://www.tiktok.com/@subecca.v' },
  'Sandra':          { instagram: 'https://www.instagram.com/sandrasiroisofficiel/', tiktok: 'https://www.tiktok.com/@sandrasiroisofficiel' },
  'Evan':            { instagram: 'https://www.instagram.com/evanvanya',             tiktok: 'https://www.tiktok.com/@evanvanyamtl' },
  'Revello':         { instagram: 'https://www.instagram.com/reviewsbyrevello/',     tiktok: 'https://www.tiktok.com/@reviewsbyrevello' },
  'Chance Twins':    { instagram: 'https://www.instagram.com/chance_twins/',         tiktok: 'https://www.tiktok.com/@chance_twins' },
  'Unemployed Bros': { instagram: 'https://www.instagram.com/unemployed.bros',       tiktok: 'https://www.tiktok.com/@unemployed.bros' },
  'Vanessa':         { instagram: 'https://www.instagram.com/vanessacene/',          tiktok: 'https://www.tiktok.com/@vanessa.cene' },
  'Zhara':           { instagram: 'https://www.instagram.com/la.lightskinn/',        tiktok: 'https://www.tiktok.com/@la.lightskin' },
  'Loizza':          { instagram: 'https://www.instagram.com/loizzaray/',            tiktok: 'https://www.tiktok.com/@loizzaray?lang=en' },
  'Ray Tarca':       { instagram: 'https://www.instagram.com/raytarca/',             tiktok: 'https://www.tiktok.com/@raytarca' },
  'Elizabeth':       { instagram: 'https://www.instagram.com/anecdotepasdepunch/',   tiktok: 'https://www.tiktok.com/@anecdotepasdepunch' },
  'Charlotte':       { instagram: 'https://www.instagram.com/charlotte.inspira',     tiktok: 'https://www.tiktok.com/@charlotte.inspira' },
  'Rebecca':         { instagram: 'https://www.instagram.com/_rwxo/',                tiktok: 'https://www.tiktok.com/@beckaa_xo' },
  'Hann-Dolf':       { instagram: 'https://www.instagram.com/vision_gourmande/',     tiktok: 'https://www.tiktok.com/@vision_gourmande' },
  'Sheldrick':       { instagram: 'https://www.instagram.com/ohshelshel/',           tiktok: 'https://www.tiktok.com/@sheldrickroc' },
  'Sarah':           { instagram: 'https://www.instagram.com/sarah_phirr/',          tiktok: 'https://www.tiktok.com/@sarah_phir/' },
}

// Default agent for creators who haven't closed deals yet
export const CREATOR_DEFAULT_AGENTS = {
  'Charlotte':        'Lydie',
  'Elizabeth':        'Lydie',
  'Loizza':           'Loizza',
  'Ray Tarca':        'Loizza',
  'Rebecca':          'Lydie',
  'Hann-Dolf':        'Steve',
  'Sheldrick':        'Lydie',
  'Sarah':            'Lydie',
}

export const AGENT_COLORS = {
  'Steve':   '#3b82f6',
  'Mirelle': '#f97316',
  'Lydie':   '#10b981',
  'Loizza':  '#8b5cf6',
  'Katrina': '#ec4899',
}

export const MONTHS_ORDER = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
]

// Compact currency for KPI cards and tables: $1.05M / $137.6K / $850.
// Two decimals at the millions tier so $10K movements stay visible.
export function fmt(n) {
  const v = Number(n) || 0
  if (!v) return '$0'
  const sign = v < 0 ? '-' : ''
  const abs = Math.abs(v)
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

export function fmtFull(n) {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0)
}

export function creatorInitials(name) {
  return (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

// Commission helper -- returns agent's personal cut from a deal
export function getAgentCommission(deal, agentName) {
  const name = (agentName || '').toLowerCase()
  if (name === 'steve')   return deal.commission_steve   || 0
  if (name === 'mirelle') return deal.commission_mirelle || 0
  if (name === 'lydie')   return deal.commission_lydie   || 0
  if (name === 'loizza')  return deal.commission_loizza  || 0
  if (name === 'vincent') return deal.commission_vincent || 0
  // Hourly agents earn no commission, so their "my commission" total is zero.
  // Without this they fell through to commission_total below and were shown
  // the agency's entire 20% as if it were theirs.
  if (name === 'katrina' || name === 'lari') return 0
  return deal.commission_total || 0
}

// Colours used for each agent's commission row.
const COMMISSION_ROW_COLORS = {
  steve:   '#3b82f6',
  mirelle: '#f97316',
  lydie:   '#10b981',
  vincent: '#eab308',
  loizza:  '#8b5cf6',
  katrina: '#64748b',
  lari:    '#64748b',
}

// Returns only the relevant commission entries for a deal based on its agent.
// Always shows the slots that SHOULD exist for that agent, even if $0 — an
// hourly agent, or a Lydie deal from before June 2026, correctly reads zero
// instead of vanishing.
//
// The agent list is NOT maintained here. It is derived from the splits in
// lib/commissions.js, which is the one place the rules are defined. This
// function used to hardcode its own if-chain per agent, which is how the
// engine and the deal detail drifted apart in the first place.
export function getRelevantCommissions(deal) {
  return commissionRowsFor(deal, key => COMMISSION_ROW_COLORS[key] || '#64748b')
}

// --- TIER / PLAN CONFIGURATION -----------------------------------------------
// Defines which features are available per pricing tier.
// 'starter' = Tier 1, 'growth' = Tier 2, 'agency' = Tier 3 (all features)

export const TIER_FEATURES = {
  starter: [
    'deals', 'deal_pipeline', 'deal_tracking',
    'crm', 'brand_crm',
    'commission_tracking',
    'basic_reports', 'csv_export', 'campaign_report',
    'creator_management',
    'privacy_mode',
    'role_based_access',
    'dashboard', 'kpi_cards', 'revenue_chart',
    'file_uploads',
  ],
  growth: [
    // Everything in starter, plus:
    'social_analytics', 'tiktok_analytics', 'instagram_analytics',
    'personal_crm', 'lead_pipeline',
    'advanced_reports', 'aging_deals', 'revenue_targets',
    'agent_performance', 'goal_tracking',
    'global_search',
    'bulk_import',
    'column_visibility', 'view_switching',
  ],
  agency: [
    // Everything in growth, plus:
    'white_label', 'custom_integrations',
    'dedicated_support', 'priority_onboarding',
  ],
}

// Check if a feature is available for a given tier
export function hasFeature(tier, feature) {
  if (!tier) return true // Default: allow all (backwards compat for GSD)
  const t = tier.toLowerCase()
  if (t === 'agency') return true // Agency gets everything
  if (t === 'growth') {
    return TIER_FEATURES.starter.includes(feature) || TIER_FEATURES.growth.includes(feature)
  }
  return TIER_FEATURES.starter.includes(feature)
}
